import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, libQs } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/* True when the signed-in user may change posters: library owner with FileJump, looking at their own library. */
export const useCanPoster = () => {
  const { user } = useAuth();
  return !!user?.permissions?.filejump && !libQs();
};

/* Poster picker for a show ({ kind: "show", key: name }) or a movie ({ kind: "video", key: id }): TMDB search, upload, remove. */
export const PosterFinder = ({ target, title, hasPoster, open, onOpenChange, onApplied }) => {
  const [cands, setCands] = useState(null);
  const [subject, setSubject] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const search = async (query = "") => {
    setCands(null);
    try {
      const { data } = await api.get("/posters/candidates", { params: { kind: target.kind, key: target.key, q: query } });
      setCands(data.candidates); setSubject(data.subject);
    } catch (e) { toast.error(formatApiError(e)); setCands([]); }
  };
  useEffect(() => { if (open && target) { setQ(""); search(); } }, [open, target?.kind, target?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (fn, label, msg) => {
    setBusy(label);
    try {
      const { data } = await fn();
      toast.success(msg);
      onApplied?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const pick = (c) => apply(() => api.post("/posters", { kind: target.kind, key: target.key, url: c.url, tmdb_id: c.tmdb_id }), c.url, `Poster saved for ${title}`);
  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", target.kind); fd.append("key", target.key); fd.append("file", file);
    apply(() => api.post("/posters/upload", fd), "upload", `Poster saved for ${title}`);
  };
  const remove = () => apply(() => api.delete("/posters", { params: { kind: target.kind, key: target.key } }), "remove", "Poster removed — the video frame is shown again");

  if (!target) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[min(94vw,56rem)] max-w-none overflow-y-auto" data-testid="poster-finder-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Poster for “{title}”</DialogTitle>
          <p className="text-sm text-zinc-500">{target.kind === "show" ? "TV show" : "Movie"}{subject?.year ? ` · ${subject.year}` : ""} · results from The Movie Database</p>
        </DialogHeader>
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); search(q); }}>
          <div className="relative min-w-[12rem] flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a different title… (add a year, e.g. Spaceballs 1987)" className="h-10 rounded-full pl-9" data-testid="poster-finder-query" />
          </div>
          <button type="submit" className="rounded-full bg-zinc-900 px-4 py-2 text-sm text-white" data-testid="poster-finder-search">Search</button>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={!!busy} className="flex items-center gap-2 rounded-full border border-zinc-300 px-4 py-2 text-sm" data-testid="poster-finder-upload">
            {busy === "upload" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}Upload image
          </button>
          {hasPoster && (
            <button type="button" onClick={remove} disabled={!!busy} className="flex items-center gap-2 rounded-full border border-red-200 px-4 py-2 text-sm text-red-700" data-testid="poster-finder-remove">
              {busy === "remove" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}Remove poster
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="poster-finder-file-input" />
        </form>
        {cands === null ? (
          <p className="flex items-center gap-2 py-10 text-sm text-zinc-500" data-testid="poster-finder-loading"><Loader2 size={14} className="animate-spin" />Searching The Movie Database…</p>
        ) : cands.length === 0 ? (
          <p className="py-10 text-center text-sm text-zinc-500" data-testid="poster-finder-empty">No posters found — try another title above (a year helps), or upload an image.</p>
        ) : (
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5" data-testid="poster-finder-results">
            {cands.map((c, i) => (
              <button key={c.tmdb_id} type="button" onClick={() => pick(c)} disabled={!!busy} title={c.overview}
                className="group text-left transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid={`poster-candidate-${i}`}>
                <div className="relative aspect-[2/3] overflow-hidden rounded-md bg-zinc-100 shadow-sm">
                  <img src={c.thumb} alt={c.title} className="h-full w-full object-cover" loading="lazy" />
                  {busy === c.url && <div className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 size={18} className="animate-spin" /></div>}
                </div>
                <p className="mt-2 truncate text-xs font-medium text-zinc-900">{c.title}</p>
                <p className="truncate text-[11px] text-zinc-500">{c.year || "—"}{c.original_title && c.original_title !== c.title ? ` · ${c.original_title}` : ""}</p>
              </button>
            ))}
          </div>
        )}
        <p className="flex items-center gap-1.5 text-[11px] text-zinc-400"><ImagePlus size={11} />A poster.jpg next to the file on FileJump is picked up automatically on the next scan. Uploaded posters always win.</p>
      </DialogContent>
    </Dialog>
  );
};
